import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Input, DatePicker, message } from 'antd';
import moment from 'moment';
import { AntDrawer } from '@/components/antd/drawer';
import { AntButton } from '@/components/antd';
import './employer-report-create.less';

const { RangePicker } = DatePicker;

interface ICompetitorItem {
    cid: string;
    keyword: string;
}

export interface INewEmployerReportData {
    reportName: string;
    reviewCompanyCid: string | number;
    reviewCompanyKeywords: string;
    reviewTimePeriod: [moment.Moment, moment.Moment];
    comparisonTimePeriod: [moment.Moment, moment.Moment];
    competitors: ICompetitorItem[];
}

export interface INewEmployerReportModalProps {
    cid: number | string;
    customerName?: string;
    visible: boolean;
    loading?: boolean;
    onClose: () => void;
    onSubmit: (data: INewEmployerReportData) => Promise<boolean | void> | boolean | void;
}

const createDefaultReviewTimeRange = (): [moment.Moment, moment.Moment] => {
    const endDate = moment().subtract(1, 'day').startOf('day');
    const startDate = endDate.clone().subtract(1, 'year');
    return [startDate, endDate];
};

const createDefaultComparisonTimeRange = (): [moment.Moment, moment.Moment] => {
    const [reviewStart, reviewEnd] = createDefaultReviewTimeRange();
    return [reviewStart.clone().subtract(1, 'year'), reviewEnd.clone().subtract(1, 'year')];
};

const sanitizeKeywordText = (value: string) =>
    value
        .split('|')
        .map(item => item.trim())
        .filter(Boolean)
        .join('|');

const getNaturalMonthCount = (start: moment.Moment, end: moment.Moment) =>
    end.clone().startOf('month').diff(start.clone().startOf('month'), 'month') + 1;

const isDateRangeOverlap = (
    first: [moment.Moment, moment.Moment],
    second: [moment.Moment, moment.Moment],
) => {
    const [start1, end1] = first;
    const [start2, end2] = second;
    return !(end1.isSameOrBefore(start2, 'day') || end2.isSameOrBefore(start1, 'day'));
};

const buildDefaultFormData = (cid: number | string): INewEmployerReportData => ({
    reportName: '',
    reviewCompanyCid: cid,
    reviewCompanyKeywords: '',
    reviewTimePeriod: createDefaultReviewTimeRange(),
    comparisonTimePeriod: createDefaultComparisonTimeRange(),
    competitors: [
        { cid: '', keyword: '' },
        { cid: '', keyword: '' },
        { cid: '', keyword: '' },
    ],
});

const NewEmployerReportModal: React.FC<INewEmployerReportModalProps> = ({
    cid,
    customerName = '',
    visible,
    loading = false,
    onClose,
    onSubmit,
}) => {
    const defaultFormData = useMemo(() => buildDefaultFormData(cid), [cid]);
    const [formData, setFormData] = useState<INewEmployerReportData>(defaultFormData);
    const [cidValidMap, setCidValidMap] = useState<Record<number, boolean | undefined>>({});

    const resetForm = useCallback(() => {
        setFormData(buildDefaultFormData(cid));
        setCidValidMap({});
    }, [cid]);

    useEffect(() => {
        if (visible) {
            resetForm();
        }
    }, [visible, resetForm]);

    useEffect(() => {
        if (formData.reviewTimePeriod[0] && formData.reviewTimePeriod[1]) {
            const startDate = formData.reviewTimePeriod[0].format('YYYY年M月D日');
            const endDate = formData.reviewTimePeriod[1].format('YYYY年M月D日');
            const reportName = `${customerName}雇主数据分析报告_${startDate}-${endDate}`;
            setFormData(prev => ({
                ...prev,
                reportName,
            }));
        }
    }, [customerName, formData.reviewTimePeriod]);

    const validateSingleTimePeriod = (startDate: moment.Moment, endDate: moment.Moment) => {
        const normalizedStart = startDate.clone().startOf('day');
        const normalizedEnd = endDate.clone().startOf('day');
        const yesterday = moment().subtract(1, 'day').startOf('day');

        if (normalizedEnd.isAfter(yesterday)) {
            message.warning('时间周期最晚只能选择到昨天');
            return false;
        }

        const minEnd = normalizedStart.clone().add(1, 'month').subtract(1, 'day');
        if (normalizedEnd.isBefore(minEnd)) {
            message.warning('时间周期最小为1个月');
            return false;
        }

        const maxEnd = normalizedStart.clone().add(1, 'year');
        if (normalizedEnd.isAfter(maxEnd)) {
            message.warning('时间周期最大为1年');
            return false;
        }

        return true;
    };

    const validateTimeCrossRules = (
        reviewPeriod: [moment.Moment, moment.Moment],
        comparisonPeriod: [moment.Moment, moment.Moment],
    ) => {
        if (
            !reviewPeriod?.[0] ||
            !reviewPeriod?.[1] ||
            !comparisonPeriod?.[0] ||
            !comparisonPeriod?.[1]
        ) {
            return true;
        }

        if (isDateRangeOverlap(reviewPeriod, comparisonPeriod)) {
            message.warning('复盘时间和对比时间重合');
            return false;
        }

        const reviewMonthCount = getNaturalMonthCount(reviewPeriod[0], reviewPeriod[1]);
        const comparisonMonthCount = getNaturalMonthCount(
            comparisonPeriod[0],
            comparisonPeriod[1],
        );
        if (reviewMonthCount !== comparisonMonthCount) {
            message.warning('复盘时间与对比时间月份数不相同');
            return false;
        }

        return true;
    };

    const handleTimePeriodChange = (
        dates: any,
        type: 'reviewTimePeriod' | 'comparisonTimePeriod',
    ) => {
        if (dates?.[0] && dates?.[1]) {
            if (!validateSingleTimePeriod(dates[0], dates[1])) {
                return;
            }
        }

        const nextData = {
            ...formData,
            [type]: dates,
        } as INewEmployerReportData;

        if (dates?.[0] && dates?.[1]) {
            if (
                !validateTimeCrossRules(
                    nextData.reviewTimePeriod,
                    nextData.comparisonTimePeriod,
                )
            ) {
                return;
            }
        }

        setFormData(nextData);
    };

    const handleCompetitorCidChange = (index: number, value: string) => {
        const numericValue = value.replace(/\D/g, '');
        setFormData(prev => {
            const next = [...prev.competitors];
            next[index] = {
                ...next[index],
                cid: numericValue,
            };
            return {
                ...prev,
                competitors: next,
            };
        });
        setCidValidMap(prev => ({
            ...prev,
            [index]: undefined,
        }));
    };

    const handleCompetitorKeywordsChange = (index: number, value: string) => {
        setFormData(prev => {
            const next = [...prev.competitors];
            next[index] = {
                ...next[index],
                keyword: value,
            };
            return {
                ...prev,
                competitors: next,
            };
        });
    };

    const validateCompetitorCid = async (index: number): Promise<boolean> => {
        const cidValue = (formData.competitors[index]?.cid || '').trim();
        if (!cidValue) {
            setCidValidMap(prev => ({
                ...prev,
                [index]: undefined,
            }));
            return true;
        }

        const [reviewStart, reviewEnd] = formData.reviewTimePeriod || [];
        if (!reviewStart || !reviewEnd) {
            setCidValidMap(prev => ({
                ...prev,
                [index]: false,
            }));
            message.warning('请先选择复盘时间周期');
            return false;
        }

        setCidValidMap(prev => ({
            ...prev,
            [index]: true,
        }));

        return true;
    };

    const handleSubmit = async () => {
        const normalizedReviewKeywords = sanitizeKeywordText(formData.reviewCompanyKeywords || '');
        if (!normalizedReviewKeywords) {
            message.warning('请输入复盘公司关键词');
            return;
        }

        if (!formData.reviewTimePeriod?.[0] || !formData.reviewTimePeriod?.[1]) {
            message.warning('请选择复盘时间周期');
            return;
        }

        if (!formData.comparisonTimePeriod?.[0] || !formData.comparisonTimePeriod?.[1]) {
            message.warning('请选择对比时间周期');
            return;
        }

        if (
            !validateTimeCrossRules(
                formData.reviewTimePeriod,
                formData.comparisonTimePeriod,
            )
        ) {
            return;
        }

        const filledCompetitors = formData.competitors
            .map((item, index) => ({
                index,
                cid: (item.cid || '').trim(),
                keyword: (item.keyword || '').trim(),
            }))
            .filter(item => item.cid || item.keyword);

        if (filledCompetitors.length === 0) {
            message.warning('请至少填写一家竞对公司');
            return;
        }

        if (filledCompetitors.length > 3) {
            message.warning('竞对公司最多支持3家');
            return;
        }

        for (const item of filledCompetitors) {
            if (!item.cid) {
                message.warning(`请填写竞对${item.index + 1}的CID`);
                return;
            }
            if (!item.keyword) {
                message.warning(`请填写竞对${item.index + 1}的关键词`);
                return;
            }
        }

        const validationResults = await Promise.all(
            filledCompetitors.map(item => {
                if (cidValidMap[item.index] === true) {
                    return Promise.resolve(true);
                }
                return validateCompetitorCid(item.index);
            }),
        );

        if (validationResults.some(result => !result)) {
            return;
        }

        Modal.confirm({
            title: '是否生成报告',
            content: '是否生成报告',
            okText: '确认',
            cancelText: '返回',
            onOk: async () => {
                const submitData: INewEmployerReportData = {
                    ...formData,
                    reviewCompanyKeywords: normalizedReviewKeywords,
                    competitors: formData.competitors.map(item => ({
                        cid: (item.cid || '').trim(),
                        keyword: (item.keyword || '').trim(),
                    })),
                };
                const result = await onSubmit(submitData);
                if (result !== false) {
                    resetForm();
                }
                return result;
            },
        });
    };

    const disableDatesAfterYesterday = (current: moment.Moment | null) =>
        Boolean(current && current >= moment().startOf('day'));

    const handleCancel = () => {
        resetForm();
        onClose();
    };

    return (
        <AntDrawer
            title="新增雇主数据分析报告"
            placement="right"
            bodyStyle={{
                overflow: 'scroll',
            }}
            width={650}
            onClose={handleCancel}
            visible={visible}
        >
            <div className="standard-drawer-form-wrapper">
                <div className="employer-report-modal-form">
                    <div className="form-item">
                        <label>报告名称：</label>
                        <Input
                            value={formData.reportName}
                            disabled
                            placeholder="报告名称将根据时间周期自动生成"
                        />
                    </div>

                    <div className="form-item">
                        <label>复盘公司CID：</label>
                        <Input value={formData.reviewCompanyCid} disabled />
                    </div>

                    <div className="form-item">
                        <label className="form-item-label">
                            <span className="form-item-required">*</span>
                            复盘公司关键词：
                        </label>
                        <Input
                            value={formData.reviewCompanyKeywords}
                            onChange={e => {
                                const value = e.target.value;
                                setFormData(prev => ({
                                    ...prev,
                                    reviewCompanyKeywords: value,
                                }));
                            }}
                            placeholder="用于筛选内容的关键词，使用 | 分隔"
                        />
                    </div>

                    <div className="form-item">
                        <label className="form-item-label">
                            <span className="form-item-required">*</span>
                            复盘时间周期：
                        </label>
                        <RangePicker
                            value={formData.reviewTimePeriod}
                            disabledDate={disableDatesAfterYesterday}
                            onChange={dates =>
                                handleTimePeriodChange(dates, 'reviewTimePeriod')
                            }
                            format="YYYY/MM/DD"
                            style={{ width: '100%' }}
                        />
                        <div className="form-hint">
                            时间范围：最少1个月，最多1年
                        </div>
                    </div>

                    <div className="form-item">
                        <label className="form-item-label">
                            <span className="form-item-required">*</span>
                            对比时间周期：
                        </label>
                        <RangePicker
                            value={formData.comparisonTimePeriod}
                            disabledDate={disableDatesAfterYesterday}
                            onChange={dates =>
                                handleTimePeriodChange(
                                    dates,
                                    'comparisonTimePeriod',
                                )
                            }
                            format="YYYY/MM/DD"
                            style={{ width: '100%' }}
                        />
                        <div className="form-hint">
                            时间范围：最少1个月，最多1年
                        </div>
                    </div>

                    <div className="form-item">
                        <label className="form-item-label">
                            <span className="form-item-required">*</span>
                            竞对公司：
                        </label>
                        <div className="competitor-grid">
                            {formData.competitors.map((item, index) => (
                                <React.Fragment key={index}>
                                    <div className="competitor-cell">
                                        <label>{`竞对${index + 1}：`}</label>
                                        <Input
                                            value={item.cid}
                                            onChange={e =>
                                                handleCompetitorCidChange(
                                                    index,
                                                    e.target.value,
                                                )
                                            }
                                            onBlur={() =>
                                                validateCompetitorCid(index)
                                            }
                                            placeholder="仅支持数字"
                                            maxLength={20}
                                        />
                                    </div>
                                    <div className="competitor-cell">
                                        <label>{`竞对${index + 1}关键词：`}</label>
                                        <Input
                                            value={item.keyword}
                                            onChange={e =>
                                                handleCompetitorKeywordsChange(
                                                    index,
                                                    e.target.value,
                                                )
                                            }
                                            placeholder="输入关键词"
                                            maxLength={200}
                                        />
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="fixed-form-btns">
                    <AntButton
                        size="large"
                        type="primary"
                        loading={loading}
                        onClick={handleSubmit}
                    >
                        提交
                    </AntButton>
                    <AntButton size="large" onClick={handleCancel}>
                        取消
                    </AntButton>
                </div>
            </div>
        </AntDrawer>
    );
};

export default NewEmployerReportModal;
